import { isTurkishAndroid } from "./native.ts";

/**
 * Android uygulaması önceden arayüzü pakete gömülü kopyadan, https://localhost
 * adresinden açıyordu; artık siteden (https://muhtesemkelime.com) açıyor.
 * Tarayıcı deposu adrese bağlı olduğu için eski yerel veriler (klasik mod
 * geçmişi, ayarlar) yeni adreste görünmez. Bu modül onları bir kez taşır:
 *
 *  1. Site ilk açılışta pakete gömülü offline.html'e (https://localhost) gider.
 *     Görevi window.name ile bildirir: Capacitor o yerel sayfayı yalnızca tam
 *     adresiyle tanıdığı için adrese # ya da ? eklenemiyor.
 *  2. O sayfa eski depoyu okuyup verileri adres parçasında (#, sunucuya hiç
 *     gitmez) siteye geri yollar.
 *  3. Site, kendisinde olmayan anahtarları yazar ve parçayı adresten siler.
 *
 * En fazla {@link MAX_MIGRATION_TRIES} açılışta denenir: ağ hatasına denk gelen
 * bir deneme veriyi kaybettirmez, ama hiç çalışmazsa da döngüye girmeden normal
 * açılışa devam edilir. Diğer modüller depoyu okumadan önce çalışmalı; bu yüzden
 * main.tsx'te ilk import.
 */

export const MIGRATION_DONE_KEY = "mk.native.migrated.v1";
export const MIGRATION_TRIED_KEY = "mk.native.migration.tried.v1";
export const MIGRATION_FRAGMENT = "mk-migrate=";
export const LEGACY_MIGRATION_URL = "https://localhost/offline.html";
export const MIGRATION_WINDOW_NAME = "mk-migrate";
export const MAX_MIGRATION_TRIES = 3;
/** offline.html'in "siteyi önbellekten bir kez açmayı dene" işareti; site açılınca silinir. */
export const OFFLINE_RETRY_WINDOW_NAME = "mk-offline-retry";

/** Adres parçasından taşınan veriyi ayıklar; bozuksa null. */
export function parseMigrationFragment(hash: string): Record<string, string> | null {
  const start = hash.indexOf(MIGRATION_FRAGMENT);
  if (start === -1) return null;
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(hash.slice(start + MIGRATION_FRAGMENT.length)));
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, string] => typeof entry[1] === "string"),
    );
  } catch {
    return {};
  }
}

/** Yeni adreste zaten olan anahtarı ezmez: yeni sürümde oynanan oyun eskisinden önemli. */
export function mergeLegacyStorage(storage: Storage, legacy: Record<string, string>): void {
  for (const [key, value] of Object.entries(legacy)) {
    if (key === MIGRATION_DONE_KEY || key === MIGRATION_TRIED_KEY) continue;
    if (storage.getItem(key) === null) storage.setItem(key, value);
  }
}

/** Taşıma tamamlandıysa ya da gerekmiyorsa false; yönlendirme başlattıysa true. */
export function runNativeMigration(win: Window = window): boolean {
  if (!isTurkishAndroid()) return false;
  if (win.name === OFFLINE_RETRY_WINDOW_NAME) win.name = "";
  try {
    const storage = win.localStorage;
    if (storage.getItem(MIGRATION_DONE_KEY)) return false;

    const legacy = parseMigrationFragment(win.location.hash);
    if (legacy) {
      mergeLegacyStorage(storage, legacy);
      storage.setItem(MIGRATION_DONE_KEY, "1");
      win.history.replaceState(win.history.state, "", win.location.pathname + win.location.search);
      return false;
    }

    // Veri gelmeden tekrar tekrar gelindiyse eski sayfa açılamıyor demektir; vazgeç.
    const tries = Number(storage.getItem(MIGRATION_TRIED_KEY)) || 0;
    if (tries >= MAX_MIGRATION_TRIES) {
      storage.setItem(MIGRATION_DONE_KEY, "1");
      return false;
    }
    storage.setItem(MIGRATION_TRIED_KEY, String(tries + 1));
    win.name = MIGRATION_WINDOW_NAME;
    win.location.replace(LEGACY_MIGRATION_URL);
    return true;
  } catch {
    return false;
  }
}
